
import type { ShareService } from '@@/bits/native_share'
import { buildShareLink, shareLinkOnService } from '@@/bits/native_share'
import { canCopyToClipboard, copyToClipboard } from 'bits/clipboard'
import device from 'bits/device'
import { isAppUsing } from 'bits/flip'
import { __ } from 'bits/intl'
import { navigateTo } from 'bits/location'
import { Component, Getter, State, Vue, namespace } from 'bits/vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import BaseIcon from 'vuecomponents/base_icon.vue'
import { ShareSubpanel } from 'vuexstore/modules/share'

const share = namespace('share')

@Component({
  components: {
    BaseIcon,
    OzIcon,
  },
})
class ShareMethods extends Vue {
  canCopyText = canCopyToClipboard
  ShareSubpanel = ShareSubpanel

  @State tenant
  @State user
  @State wall
  @State isElectronApp
  @State isSkoletubeUser
  @Getter title
  @Getter description
  @Getter publicKey
  @Getter isPrivate
  @Getter isPasswordProtected
  @Getter isLoggedInUsersOnly
  @Getter isOrgWide
  @Getter isLibraryWide
  @Getter isSchoolLibrary
  @Getter isClassroomLibrary
  @share.Action changeActivePanel
  @share.Action openQrModal
  @namespace('snackbars').Action setSnackbar

  buildShareLink = buildShareLink

  get shouldShowZoom() {
    return isAppUsing('zoom') && device.desktop
  }

  get isBackpackPadlet() {
    return this.tenant.type === 'school'
  }

  get isCurrentUserTeacher() {
    return this.isBackpackPadlet && ['teacher', 'owner'].includes(this.user.role)
  }

  get isLtiConsumable() {
    return this.tenant.is_lti_ready
  }

  get isLibraryWideBannerText() {
    if (this.isSchoolLibrary)
      return __(
        '<strong>This padlet is restricted to users in your school.</strong> Visitors not in your school will be denied access.',
      )
    if (this.isClassroomLibrary)
      return __(
        '<strong>This padlet is restricted to users in your classroom.</strong> Visitors not in your classroom will be denied access.',
      )
    return __(
      '<strong>This padlet is restricted to users in your team.</strong> Visitors not in your team will be denied access.',
    )
  }

  openSlideshow() {
    const slideshowUrl = `${this.wall.links.show}/slideshow`
    navigateTo(slideshowUrl, { target: '_blank' })
  }

  openZoom() {
    const zoomUrl = `/zoom/open_wall?wall_id=${this.wall.id}`
    navigateTo(zoomUrl, { target: '_blank' })
  }

  async copyLink() {
    const result = await copyToClipboard(this.wall.links.doodle)
    if (result) {
      this.setSnackbar({ message: __('Copied padlet URL to your clipboard.') })
    } else {
      this.setSnackbar({ message: __('Could not copy padlet URL to your clipboard.') })
    }
  }

  getShareLinkFor(service: ShareService): string {
    return this.buildShareLink({
      service,
      ...{
        url: this.wall.links.doodle,
        title: service === 'email' ? __('Hey, check out what I have created at Padlet') : this.title,
        description: service === 'email' ? __('Made with love') + ' ' + this.wall.links.doodle : this.description,
        media: this.wall.links.snap,
        publicKey: this.publicKey,
      },
    })
  }

  shareOnService(service: ShareService) {
    shareLinkOnService(this.getShareLinkFor(service), service)
  }
}

export default ShareMethods
